import { 
  Currency, 
  ExchangeRate, 
  ConversionResult, 
  HistoricalRate, 
  RateAlert,
  APIProvider,
  CacheEntry,
  FeeStructure
} from '@/types/currency'
import { formatCurrency, generateId, calculateMedian } from '@utils/formatters'
import { APIService } from './APIService'
import { CacheService } from './CacheService'
import { NotificationService } from './NotificationService'

/**
 * Core currency conversion engine with multi-provider support,
 * intelligent caching, and comprehensive rate management
 */
export class CurrencyConverter {
  private apiService: APIService
  private cacheService: CacheService
  private notificationService: NotificationService
  private rateProviders: Map<string, APIProvider>
  private rateCache: Map<string, CacheEntry<ExchangeRate>>
  private subscriptions: Set<string>
  private alerts: RateAlert[]
  private watchlist: string[]
  private isInitialized: boolean = false

  constructor() {
    this.apiService = new APIService()
    this.cacheService = new CacheService()
    this.notificationService = new NotificationService()
    this.rateProviders = new Map()
    this.rateCache = new Map()
    this.subscriptions = new Set()
    this.alerts = []
    this.watchlist = []
    
    this.initializeProviders()
  }

  /**
   * Initialize API providers with fallback hierarchy
   */
  private initializeProviders(): void {
    const providers: APIProvider[] = [
      {
        name: 'ExchangeRate-API',
        baseUrl: 'https://api.exchangerate-api.com/v4',
        rateLimit: 1000,
        isActive: true,
        priority: 1
      },
      {
        name: 'Fixer.io',
        baseUrl: 'https://api.fixer.io',
        apiKey: import.meta.env?.VITE_FIXER_API_KEY,
        rateLimit: 100,
        isActive: true,
        priority: 2
      },
      {
        name: 'CurrencyAPI',
        baseUrl: 'https://api.currencyapi.com/v3',
        apiKey: import.meta.env?.VITE_CURRENCY_API_KEY,
        rateLimit: 300,
        isActive: true,
        priority: 3
      }
    ]

    providers.forEach(provider => {
      this.rateProviders.set(provider.name, provider)
    })

    this.isInitialized = true
  }

  /**
   * Get exchange rate with multi-provider fallback and validation
   */
  async getExchangeRate(
    fromCurrency: string, 
    toCurrency: string, 
    options: {
      maxAge?: number
      forceRefresh?: boolean
      includeBidAsk?: boolean
    } = {}
  ): Promise<ExchangeRate> {
    if (!this.isInitialized) {
      throw new CurrencyError({
        code: 'NOT_INITIALIZED',
        message: 'Currency converter not initialized',
        timestamp: new Date()
      })
    }

    const cacheKey = `${fromCurrency}-${toCurrency}`
    const maxAge = options.maxAge || 300000 // 5 minutes default
    
    // Check cache first unless force refresh
    if (!options.forceRefresh) {
      const cached = this.rateCache.get(cacheKey)
      if (cached && this.isCacheValid(cached, maxAge)) {
        return cached.data
      }
    }

    try {
      // Fetch from multiple providers for accuracy
      const ratePromises = Array.from(this.rateProviders.values())
        .filter(provider => provider.isActive)
        .sort((a, b) => a.priority - b.priority)
        .map(provider => this.fetchFromProvider(provider, fromCurrency, toCurrency))

      const results = await Promise.allSettled(ratePromises)
      const validRates = results
        .filter((result): result is PromiseFulfilledResult<ExchangeRate> => 
          result.status === 'fulfilled'
        )
        .map(result => result.value)

      if (validRates.length === 0) {
        throw new CurrencyError({
          code: 'NO_PROVIDERS_AVAILABLE',
          message: 'Unable to fetch exchange rates from any provider',
          timestamp: new Date()
        })
      }

      // Use consensus rate for accuracy (median of valid rates)
      const consensusRate = this.calculateConsensusRate(validRates)
      
      // Cache the result
      this.rateCache.set(cacheKey, {
        data: consensusRate,
        timestamp: Date.now(),
        ttl: maxAge,
        source: 'consensus'
      })

      // Check alerts for this currency pair
      this.checkRateAlerts(fromCurrency, toCurrency, consensusRate.rate)

      return consensusRate
    } catch (error) {
      // Try to return cached data if available, even if stale
      const cached = this.rateCache.get(cacheKey)
      if (cached) {
        console.warn(`Using stale cache data for ${cacheKey}:`, error)
        return cached.data
      }
      
      throw error
    }
  }

  /**
   * Convert amount between currencies with comprehensive result
   */
  async convert(
    amount: number,
    fromCurrency: string,
    toCurrency: string,
    options: {
      precision?: number
      feeStructure?: FeeStructure
      includeAnalysis?: boolean
    } = {}
  ): Promise<ConversionResult> {
    if (amount <= 0) {
      throw new CurrencyError({
        code: 'INVALID_AMOUNT',
        message: 'Amount must be greater than zero',
        timestamp: new Date()
      })
    }

    const exchangeRate = await this.getExchangeRate(fromCurrency, toCurrency)
    const convertedAmount = amount * exchangeRate.rate
    
    // Apply fees if specified
    let fees: FeeStructure | undefined
    let finalAmount = convertedAmount
    
    if (options.feeStructure) {
      fees = this.calculateFees(amount, convertedAmount, options.feeStructure)
      finalAmount = convertedAmount - fees.total
    }

    const precision = options.precision || this.getDefaultPrecision(toCurrency)
    
    return {
      originalAmount: amount,
      convertedAmount: finalAmount,
      formattedAmount: formatCurrency(finalAmount, toCurrency, precision),
      exchangeRate: exchangeRate.rate,
      fromCurrency: await this.getCurrencyInfo(fromCurrency),
      toCurrency: await this.getCurrencyInfo(toCurrency),
      timestamp: new Date(),
      fees,
      rateSource: exchangeRate.source
    }
  }

  /**
   * Get historical exchange rates for charting and analysis
   */
  async getHistoricalRates(
    fromCurrency: string,
    toCurrency: string,
    startDate: Date,
    endDate: Date,
    interval: 'daily' | 'weekly' | 'monthly' = 'daily'
  ): Promise<HistoricalRate[]> {
    const cacheKey = `hist-${fromCurrency}-${toCurrency}-${startDate.toISOString()}-${endDate.toISOString()}-${interval}`
    const cached = this.cacheService.get<HistoricalRate[]>(cacheKey)
    
    if (cached && this.isHistoricalCacheValid(cached)) {
      return cached.data
    }

    try {
      const historicalData = await this.apiService.getHistoricalRates(
        fromCurrency,
        toCurrency,
        startDate,
        endDate,
        interval
      )

      // Cache historical data (longer TTL since it doesn't change)
      this.cacheService.set(cacheKey, historicalData, 24 * 60 * 60 * 1000) // 24 hours

      return historicalData
    } catch (error) {
      console.error('Failed to fetch historical rates:', error)
      throw new CurrencyError({
        code: 'HISTORICAL_DATA_ERROR',
        message: 'Unable to fetch historical exchange rates',
        timestamp: new Date(),
        details: error
      })
    }
  }

  /**
   * Set up rate alert with multiple notification methods
   */
  setupRateAlert(
    fromCurrency: string,
    toCurrency: string,
    targetRate: number,
    condition: 'above' | 'below' | 'equals',
    notificationMethods: any[] = []
  ): RateAlert {
    const alert: RateAlert = {
      id: generateId(),
      fromCurrency,
      toCurrency,
      targetRate,
      condition,
      createdAt: new Date(),
      isActive: true,
      triggered: false,
      notificationMethods
    }

    this.alerts.push(alert)
    this.subscribeToRateUpdates(fromCurrency, toCurrency)

    return alert
  }

  /**
   * Bulk conversion for multiple currency pairs
   */
  async convertMultiple(
    amount: number,
    fromCurrency: string,
    toCurrencies: string[]
  ): Promise<ConversionResult[]> {
    const conversionPromises = toCurrencies.map(toCurrency =>
      this.convert(amount, fromCurrency, toCurrency)
    )

    return Promise.all(conversionPromises)
  }

  /**
   * Get supported currencies list
   */
  async getSupportedCurrencies(): Promise<Currency[]> {
    return this.apiService.getSupportedCurrencies()
  }

  /**
   * Add currency pair to watchlist
   */
  addToWatchlist(fromCurrency: string, toCurrency: string): void {
    const pair = `${fromCurrency}-${toCurrency}`
    if (!this.watchlist.includes(pair)) {
      this.watchlist.push(pair)
      this.subscribeToRateUpdates(fromCurrency, toCurrency)
    }
  }

  /**
   * Remove currency pair from watchlist
   */
  removeFromWatchlist(fromCurrency: string, toCurrency: string): void {
    const pair = `${fromCurrency}-${toCurrency}`
    const index = this.watchlist.indexOf(pair)
    if (index > -1) {
      this.watchlist.splice(index, 1)
    }
  }

  /**
   * Get current watchlist with live rates
   */
  async getWatchlistWithRates(): Promise<Array<{
    pair: string
    rate: ExchangeRate
    change24h?: number
  }>> {
    const watchlistPromises = this.watchlist.map(async (pair) => {
      const [from, to] = pair.split('-')
      const rate = await this.getExchangeRate(from, to)
      return { pair, rate }
    })

    return Promise.all(watchlistPromises)
  }

  // Private helper methods

  private async fetchFromProvider(
    provider: APIProvider,
    fromCurrency: string,
    toCurrency: string
  ): Promise<ExchangeRate> {
    return this.apiService.fetchExchangeRate(provider, fromCurrency, toCurrency)
  }

  private calculateConsensusRate(rates: ExchangeRate[]): ExchangeRate {
    if (rates.length === 1) {
      return rates[0]
    }

    const rateValues = rates.map(r => r.rate)
    const medianRate = calculateMedian(rateValues)
    
    // Calculate confidence based on rate agreement
    const maxDeviation = Math.max(...rateValues.map(r => Math.abs(r - medianRate)))
    const confidence = Math.max(0, 1 - (maxDeviation / medianRate))

    return {
      fromCurrency: rates[0].fromCurrency,
      toCurrency: rates[0].toCurrency,
      rate: medianRate,
      timestamp: Date.now(),
      source: `consensus-${rates.length}`,
      confidence
    }
  }

  private isCacheValid(cache: CacheEntry<any>, maxAge: number): boolean {
    return Date.now() - cache.timestamp < maxAge
  }

  private isHistoricalCacheValid(cache: CacheEntry<any>): boolean {
    return Date.now() - cache.timestamp < cache.ttl
  }

  private calculateFees(
    originalAmount: number,
    convertedAmount: number,
    feeStructure: Partial<FeeStructure>
  ): FeeStructure {
    let total = 0
    
    if (feeStructure.percentage) {
      total += convertedAmount * (feeStructure.percentage / 100)
    }
    
    if (feeStructure.fixed) {
      total += feeStructure.fixed
    }

    return {
      percentage: feeStructure.percentage,
      fixed: feeStructure.fixed,
      total,
      description: feeStructure.description || 'Transaction fees'
    }
  }

  private async getCurrencyInfo(currencyCode: string): Promise<Currency> {
    // This would typically fetch from a currency metadata service
    // For now, return basic info
    return {
      code: currencyCode,
      name: currencyCode, // Would be proper name in real implementation
      symbol: this.getCurrencySymbol(currencyCode)
    }
  }

  private getCurrencySymbol(currencyCode: string): string {
    const symbols: Record<string, string> = {
      USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$',
      AUD: 'A$', CHF: 'Fr', CNY: '¥', INR: '₹', BRL: 'R$'
    }
    return symbols[currencyCode] || currencyCode
  }

  private getDefaultPrecision(currency: string): number {
    // Some currencies have different decimal precision
    const lowPrecisionCurrencies = ['JPY', 'KRW', 'VND', 'IDR']
    return lowPrecisionCurrencies.includes(currency) ? 0 : 2
  }

  private subscribeToRateUpdates(fromCurrency: string, toCurrency: string): void {
    const pair = `${fromCurrency}-${toCurrency}`
    this.subscriptions.add(pair)
  }

  private checkRateAlerts(fromCurrency: string, toCurrency: string, currentRate: number): void {
    const relevantAlerts = this.alerts.filter(alert =>
      alert.fromCurrency === fromCurrency &&
      alert.toCurrency === toCurrency &&
      alert.isActive &&
      !alert.triggered
    )

    relevantAlerts.forEach(alert => {
      let shouldTrigger = false

      switch (alert.condition) {
        case 'above':
          shouldTrigger = currentRate > alert.targetRate
          break
        case 'below':
          shouldTrigger = currentRate < alert.targetRate
          break
        case 'equals':
          // Allow small tolerance for "equals"
          const tolerance = alert.targetRate * 0.001 // 0.1% tolerance
          shouldTrigger = Math.abs(currentRate - alert.targetRate) <= tolerance
          break
      }

      if (shouldTrigger) {
        this.triggerAlert(alert, currentRate)
      }
    })
  }

  private triggerAlert(alert: RateAlert, currentRate: number): void {
    alert.triggered = true
    alert.triggeredAt = new Date()

    // Send notifications
    this.notificationService.sendAlertNotification(alert, currentRate)
  }
}

// Custom error class for currency-related errors
class CurrencyError extends Error {
  public code: string
  public timestamp: Date
  public source?: string
  public details?: any

  constructor(config: {
    code: string
    message: string
    timestamp: Date
    source?: string
    details?: any
  }) {
    super(config.message)
    this.name = 'CurrencyError'
    this.code = config.code
    this.timestamp = config.timestamp
    this.source = config.source
    this.details = config.details
  }
}

export { CurrencyError }